export type ProjectCard = {
  id: string
  name: string
  phase: string
  mvp_ready: boolean
  risk: string
  owner: string
  last_event_at?: string | null
  requirement_coverage_pct?: number
  skill_grade_avg?: number | null
  cost_to_date?: number | null
  status?: string
  loop?: number
}

export type FactoryOpsAlert = {
  id: string
  name: string
  severity: string
  status: string
  value: number
  threshold: number
  summary: string
}

export type FactoryOpsCard = {
  id: string
  label: string
  value: string | number
  hint: string
  tone: string
}

export type FactoryOps = {
  available: boolean
  reason?: string | null
  metrics?: Record<string, unknown>
  funnel?: { stage: string; count: number }[]
  alerts?: FactoryOpsAlert[]
  cards?: FactoryOpsCard[]
  slo?: Record<string, number>
  generated_at?: string
}

export type DashboardSnapshot = {
  project_cards: ProjectCard[]
  graphs: {
    mvp_ready_by_build: { build_id: string; mvp_ready: number }[]
    mean_skill_grade_by_build: { build_id: string; mean_grade: number }[]
    ai_cost_by_model?: { model?: string; cost_usd?: number }[]
    sdlc_funnel?: { stage: string; count: number }[]
  }
  metric_families: string[]
  factory_ops?: FactoryOps | null
}

export async function fetchDashboard(): Promise<DashboardSnapshot> {
  const res = await fetch('/api/afos360/dashboard', { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to load dashboard')
  return res.json()
}

export async function fetchRoutes(): Promise<
  { phase_id: string; tier: string; provider: string; model: string; weight_source: string }[]
> {
  const res = await fetch('/api/afos360/routes', { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to load routes')
  return res.json()
}

export type CockpitNotification = {
  id: string
  kind: string
  severity: string
  title: string
  body?: string
  href?: string
  build_id?: string
}

export type CockpitNotifications = {
  unread_alerts: number
  notifications: CockpitNotification[]
}

export type BuildCockpitDetail = {
  build_id: string
  card: ProjectCard
  analytics: {
    success_pct: number
    grade: string
    mvp_ready: boolean
    checks?: Record<string, boolean>
    recommendations?: string[]
    breakdown?: { metric: string; weight: number; passed: boolean }[]
  }
  phases: { id?: string; status?: string; tier?: string; finished_at?: string }[]
  skill_grades: {
    phase_id?: string
    mean?: number
    promote_eligible?: boolean
    scores?: Record<string, number>
  }[]
  mean_skill_grade?: number | null
  mvp_status: {
    status?: string
    mvp_ready?: boolean
    loop?: number
    idea?: string
    finished_at?: string
    updated_at?: string
  }
  related_sdlc: { run_id?: string; status?: string; ticket_subject?: string }[]
  related_ephemeral: {
    eph_id?: string
    status?: string
    signed_off?: boolean
    preview_url?: string
  }[]
  artifacts: string[]
  links: Record<string, string>
}

export async function fetchCockpitNotifications(): Promise<CockpitNotifications> {
  const res = await fetch('/api/afos360/cockpit/notifications', {
    credentials: 'include',
  })
  if (!res.ok) throw new Error('Failed to load notifications')
  return res.json()
}

export async function fetchBuildCockpit(buildId: string): Promise<BuildCockpitDetail> {
  const res = await fetch(
    `/api/afos360/builds/${encodeURIComponent(buildId)}/cockpit`,
    { credentials: 'include' },
  )
  if (!res.ok) throw new Error('Failed to load build cockpit')
  return res.json()
}

// --- Executive overview + pending human gates (AFOS-41) -------------------

export type GateKindMeta = { label: string; decide_at: string; who: string }

export type PendingGate = {
  id: string
  kind: string
  label: string
  title: string
  subject: string
  since?: string | null
  href: string
  decide_at: string
  who: string
}

export type PendingGates = {
  total: number
  by_kind: Record<string, number>
  kinds: Record<string, GateKindMeta>
  items: PendingGate[]
  kind: string | null
  matching: number
  offset: number
  next_offset: number | null
  truncated: boolean
  unavailable: Record<string, string>
  incomplete: string[]
  restricted: Record<string, string>
  generated_at: string
}

export type ExecutiveOverview = {
  generated_at: string
  cases: {
    total: number
    by_state: { state: string; count: number }[]
    outcomes: Record<string, number>
    stale_outcomes: number
  } | null
  runs: {
    total: number
    by_status: { status: string; count: number }[]
    started_7d: number
    started_24h: number
    merge_approved: number
    active: number
  } | null
  budgets: {
    source?: string
    limits: Record<string, number>
    usage: {
      concurrent: { used: number; limit?: number }
      per_day: { used: number; limit?: number }
    } | null
  } | null
  squads: { count: number } | null
  gates: { total: number; by_kind: Record<string, number>; kinds: Record<string, GateKindMeta> }
  unavailable: Record<string, string>
  incomplete: string[]
  restricted: Record<string, string>
}

export async function getJson<T>(path: string, what: string): Promise<T> {
  const res = await fetch(path, { credentials: 'include' })
  if (!res.ok) {
    let detail = ''
    try {
      detail = (await res.json())?.detail ?? ''
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail ? `${what}: ${detail}` : `${what} (HTTP ${res.status})`)
  }
  return res.json() as Promise<T>
}

export function fetchExecutiveOverview(): Promise<ExecutiveOverview> {
  return getJson('/api/afos360/v1/overview', 'Failed to load the overview')
}

export function fetchPendingGates(kind?: string | null, offset = 0): Promise<PendingGates> {
  const q = new URLSearchParams({ offset: String(offset), limit: '50' })
  if (kind) q.set('kind', kind)
  return getJson(`/api/afos360/v1/gates/pending?${q}`, 'Failed to load pending approvals')
}

// --- Learning, evaluations, engineering, platform admin (AFOS-41 part 2) ---

export async function sendJson<T>(
  path: string,
  method: 'POST' | 'PUT',
  body: unknown,
  what: string,
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  })
  if (!res.ok) {
    let detail = ''
    try {
      detail = (await res.json())?.detail ?? ''
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail ? `${what}: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}` : `${what} (HTTP ${res.status})`)
  }
  return res.json() as Promise<T>
}

export type SkillValidationRow = {
  skill_id: string
  manifest: boolean
  scenarios: boolean
  last_passed?: boolean | null
  last_validated_at?: string | null
  content_digest?: string | null
  validated_digest?: string | null
  stale: boolean
  reviewed: boolean
  reviewed_by?: string | null
  publishable: boolean
}

export type SkillValidationSummary = {
  skills: SkillValidationRow[]
  with_manifest: number
  total: number
  assurance?: Record<string, unknown>
}

export type SkillEvolutionRow = {
  evolution_id: string
  status: string
  title?: string | null
  skill_key?: string | null
  generation?: number | null
  confidence?: number | null
  created_at?: string | null
  promoted_at?: string | null
  body_md?: string | null
  body_sha256?: string | null
}

export type SkillEvolutionOverview = {
  quarantined: number
  promoted: number
  evolutions: SkillEvolutionRow[]
  policy?: Record<string, unknown>
}

export type FleetScorecards = {
  page: {
    offset?: number
    limit?: number
    next_offset?: number | null
    total_squads: number
    complete: boolean
    deferred?: unknown[]
  }
  totals: {
    squads: number
    graded: number
    grades: Record<string, number>
    promotion_candidates: string[]
    regressed: string[]
  }
  ranking: { team_id: string; name?: string | null; tier?: string | null; score?: number | null; grade?: string | null }[]
  generated_at: string
}

export type EngineeringHandoff = {
  case_id: string
  ticket?: string | null
  source_system?: string | null
  decision: string
  decision_stale: boolean
  run_id?: string | null
  run_status?: string | null
  run_stage?: string | null
  merge_approved: boolean
  proposals: number
  proposal_statuses: string[]
  outcome_status?: string | null
  outcome_stale: boolean
  state: string
  updated_at?: string | null
}

export type EngineeringBoard = {
  generated_at: string
  handoffs: EngineeringHandoff[]
  totals: { handoffs: number; with_run: number; merge_approved: number; with_proposal: number; resolved: number }
  runs_without_case: { run_id: string; status?: string; subject?: string | null; stage?: string | null; created_at?: string | null }[]
  unavailable: Record<string, string>
  incomplete: string[]
}

export type BudgetEffective = {
  limits: Record<string, number>
  source: string
  set_by?: string | null
  set_at?: string | null
}

export type InferenceStatus = {
  mode: string
  region?: string | null
  bedrock?: Record<string, unknown> | null
  byok?: Record<string, { configured: boolean; source?: string | null }>
  sample_routes?: unknown[]
}

export type ModelRoute = { phase_id: string; tier: string; provider: string; model: string; weight_source: string }

export type PlatformEvent = {
  event_id: string
  type: string
  occurred_at: string
  actor?: string | null
  build_id?: string | null
}

export const fetchSkillValidation = () =>
  getJson<SkillValidationSummary>('/api/afos360/v1/skill-validation', 'Failed to load skill validation')
export const fetchSkillEvolutions = () =>
  getJson<SkillEvolutionOverview>('/api/afos360/v1/skill-evolutions', 'Failed to load skill evolutions')
/** Every page of fleet scorecards, merged. The server pages by squad with a
 *  per-request scoring budget; squads it defers are counted, not dropped. */
export type FleetScorecardsAll = FleetScorecards & { pages: number; deferred: number; scanned: number }

export async function fetchFleetScorecards(maxPages = 50): Promise<FleetScorecardsAll> {
  let offset = 0
  let pages = 0
  let deferred = 0
  let last: FleetScorecards | null = null
  const grades: Record<string, number> = {}
  const candidates: string[] = []
  const regressed: string[] = []
  const ranking: FleetScorecards['ranking'] = []
  let squads = 0
  let graded = 0
  while (pages < maxPages) {
    const page = await getJson<FleetScorecards>(
      `/api/afos360/v1/squads/scorecards?offset=${offset}&limit=100`,
      'Failed to load squad scorecards',
    )
    pages += 1
    last = page
    squads += page.totals.squads
    graded += page.totals.graded
    for (const [g, n] of Object.entries(page.totals.grades)) grades[g] = (grades[g] ?? 0) + n
    candidates.push(...page.totals.promotion_candidates)
    regressed.push(...page.totals.regressed)
    ranking.push(...page.ranking)
    deferred += Array.isArray(page.page.deferred) ? page.page.deferred.length : 0
    const next = page.page.next_offset
    if (next == null || next <= offset) break
    offset = next
  }
  ranking.sort((a, b) => (b.score ?? -1) - (a.score ?? -1))
  const total = last?.page.total_squads ?? squads
  return {
    page: {
      total_squads: total,
      complete: deferred === 0 && squads >= total,
      next_offset: null,
    },
    totals: { squads, graded, grades, promotion_candidates: candidates, regressed },
    ranking,
    generated_at: last?.generated_at ?? new Date().toISOString(),
    pages,
    deferred,
    scanned: squads,
  }
}
export const fetchEngineeringBoard = () =>
  getJson<EngineeringBoard>('/api/afos360/v1/engineering', 'Failed to load the engineering board')
export const fetchBudgets = () => getJson<BudgetEffective>('/api/afos360/v1/budgets', 'Failed to load budgets')
export const fetchInference = () => getJson<InferenceStatus>('/api/afos360/inference', 'Failed to load inference status')
export const fetchModelRoutes = () => getJson<ModelRoute[]>('/api/afos360/routes', 'Failed to load model routes')
export const fetchRecentEvents = (limit = 25) =>
  getJson<{ events: PlatformEvent[]; count: number; truncated: boolean }>(
    `/api/afos360/v1/events?limit=${limit}`,
    'Failed to load recent activity',
  )
